import React, { useEffect, useState } from 'react';
import * as echarts from 'echarts';
import { fetchUsageStats } from '../services/api';

const ConversationChart = ({ weeklyData }) => {
  useEffect(() => {
    const chartDom = document.getElementById('conversationChart');
    if (chartDom && weeklyData && weeklyData.length > 0) {
      const myChart = echarts.init(chartDom);
      
      // Format data for the chart
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const chartData = weeklyData.map(day => day.count);
      const chartLabels = weeklyData.map(day => {
        const date = new Date(day.date);
        return `${days[date.getDay()]} ${date.getMonth() + 1}/${date.getDate()}`;
      });

      const option = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          backgroundColor: '#1f2937',
          borderColor: '#374151',
          textStyle: {
            color: '#f3f4f6'
          },
          formatter: function(params) {
            const data = params[0];
            return `${data.name}<br/>Conversations: ${data.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: chartLabels,
          axisLine: {
            lineStyle: {
              color: '#4b5563'
            }
          },
          axisLabel: {
            color: '#9ca3af'
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: '#4b5563'
            }
          },
          axisLabel: {
            color: '#9ca3af'
          },
          splitLine: {
            lineStyle: {
              color: '#374151'
            }
          }
        },
        series: [
          {
            name: 'Conversations',
            type: 'line',
            stack: 'Total',
            data: chartData,
            smooth: true,
            lineStyle: {
              color: '#6366f1'
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: 'rgba(99, 102, 241, 0.5)'
                },
                {
                  offset: 1,
                  color: 'rgba(99, 102, 241, 0.05)'
                }
              ])
            },
            itemStyle: {
              color: '#6366f1'
            }
          }
        ]
      };

      myChart.setOption(option);

      const handleResize = () => {
        myChart.resize();
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        myChart.dispose();
      };
    }
  }, [weeklyData]);

  return <div id="conversationChart" className="w-full h-80"></div>;
};

const StatCard = ({ title, value, change, changeType, icon, iconBg, iconColor, showChange = true }) => {
  const changeColorClass = changeType === 'positive' ? 'text-green-400' : 'text-red-400';

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-gray-400 text-sm font-medium mb-2">{title}</h2>
          <div className="text-4xl font-bold text-white">{value}</div>
          {showChange && (
            <p className={`${changeColorClass} text-sm mt-2`}>
              <span className="font-medium">{change}</span> from last period
            </p>
          )}
        </div>
        <div className={`${iconBg} p-4 rounded-full`}>
          <i className={`${icon} ${iconColor} text-2xl`}></i>
        </div>
      </div>
    </div>
  );
};

// Helper function to format seconds to MM:SS
const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Helper function to format date for comparison (YYYY-MM-DD)
const formatDateForComparison = (date) => {
  return date.toISOString().split('T')[0];
};

// Helper function to generate last 7 days including today
const generateLast7Days = () => {
  const days = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    days.push(formatDateForComparison(date));
  }
  
  return days;
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [totalConversations, setTotalConversations] = useState(0);
  const [averageDuration, setAverageDuration] = useState('0:00');
  const [weeklyData, setWeeklyData] = useState([]);
  const [weekChange, setWeekChange] = useState('+0%');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch usage stats from the API
        const response = await fetchUsageStats();
        
        if (response.status === 'success') {
          const { interaction_stats, average_call_duration, total_call_duration } = response;
          
          // Generate exactly the last 7 days
          const last7Days = generateLast7Days();
          
          // Create a map for quick lookup of conversation counts
          const statsMap = new Map();
          interaction_stats.forEach(stat => {
            const dateKey = formatDateForComparison(new Date(stat.day));
            statsMap.set(dateKey, stat.interaction_count);
          });
          
          // Prepare chart data for exactly the last 7 days
          const chartData = last7Days.map(dateKey => ({
            date: dateKey,
            count: statsMap.get(dateKey) || 0 // Use 0 if no data for that day
          }));
          
          // Calculate total conversations for the last 7 days
          const totalConversationsCount = chartData.reduce((sum, day) => sum + day.count, 0);
          
          // Calculate change comparing first 3 days vs last 4 days of the week
          const firstHalf = chartData.slice(0, 3);
          const secondHalf = chartData.slice(3);
          
          const firstHalfTotal = firstHalf.reduce((sum, day) => sum + day.count, 0);
          const secondHalfTotal = secondHalf.reduce((sum, day) => sum + day.count, 0);
          
          let weekChangePercent = 0;
          if (firstHalfTotal > 0) {
            weekChangePercent = ((secondHalfTotal - firstHalfTotal) / firstHalfTotal) * 100;
          } else if (secondHalfTotal > 0) {
            weekChangePercent = 100;
          }
          
          // Update state
          setTotalConversations(totalConversationsCount);
          setAverageDuration(formatDuration(Math.round(average_call_duration || 0)));
          setWeeklyData(chartData);
          setWeekChange(weekChangePercent >= 0 ? `+${weekChangePercent.toFixed(1)}%` : `${weekChangePercent.toFixed(1)}%`);
          
        } else {
          console.error('API returned error status:', response.status);
          // Set default values on error - still show last 7 days with 0 counts
          const last7Days = generateLast7Days();
          const defaultChartData = last7Days.map(dateKey => ({
            date: dateKey,
            count: 0
          }));
          
          setTotalConversations(0);
          setAverageDuration('0:00');
          setWeeklyData(defaultChartData);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set default values on error - still show last 7 days with 0 counts
        const last7Days = generateLast7Days();
        const defaultChartData = last7Days.map(dateKey => ({
          date: dateKey,
          count: 0
        }));
        
        setTotalConversations(0);
        setAverageDuration('0:00');
        setWeeklyData(defaultChartData);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = [
    {
      title: 'TOTAL CONVERSATIONS of last week',
      value: loading ? '...' : totalConversations.toLocaleString(),
      change: weekChange,
      changeType: weekChange.startsWith('+') ? 'positive' : 'negative',
      icon: 'fas fa-comments',
      iconBg: 'bg-indigo-900/50',
      iconColor: 'text-indigo-400',
      showChange: true
    },
    {
      title: 'AVERAGE DURATION',
      value: loading ? '...' : averageDuration,
      change: '',
      changeType: 'positive',
      icon: 'fas fa-clock',
      iconBg: 'bg-purple-900/50',
      iconColor: 'text-purple-400',
      showChange: false
    }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6 text-white">Dashboard Overview</h1>
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6 text-white">Dashboard Overview</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      <div className="mt-8 bg-gray-800 rounded-lg p-6 shadow-lg">
        <h2 className="text-xl font-bold mb-4 text-white">
          Conversation Trends (Last 7 Days)
        </h2>
        <ConversationChart weeklyData={weeklyData} />
      </div>
    </div>
  );
};

export default Dashboard;
